export type ModelKind = "controller" | "headphones" | "bubble";
type Vec3 = [number, number, number];
type Point = [number, number];
type Color = [number, number, number];

function color(hex: string): Color {
  return [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255) as Color;
}

/** Small, untextured solid meshes, with position, normal and color interleaved per vertex. */
class Mesh {
  vertices: number[] = [];

  triangle(a: Vec3, b: Vec3, c: Vec3, tint: Color) {
    const u: Vec3 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
    const v: Vec3 = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
    const normal: Vec3 = [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]];
    const length = Math.hypot(...normal) || 1;
    for (const point of [a, b, c]) this.vertices.push(...point, ...normal.map((n) => n / length), ...tint);
  }

  solid(outline: Point[], depth: number, tint: string, origin: Vec3 = [0, 0, 0], bevel = 0.07) {
    const rgb = color(tint);
    const point = (xy: Point, scale: number, z: number): Vec3 => [xy[0] * scale + origin[0], xy[1] * scale + origin[1], z + origin[2]];
    const rings = [
      { scale: 0.94, z: -depth / 2 },
      { scale: 1, z: -depth / 2 + bevel },
      { scale: 1, z: depth / 2 - bevel },
      { scale: 0.94, z: depth / 2 },
    ];
    for (const [i, current] of outline.entries()) {
      const next = outline[(i + 1) % outline.length]!;
      this.triangle([origin[0], origin[1], origin[2] + depth / 2], point(current, 0.94, depth / 2), point(next, 0.94, depth / 2), rgb);
      this.triangle([origin[0], origin[1], origin[2] - depth / 2], point(next, 0.94, -depth / 2), point(current, 0.94, -depth / 2), rgb);
      for (let ring = 0; ring < rings.length - 1; ring++) {
        const from = rings[ring]!;
        const to = rings[ring + 1]!;
        const a = point(current, from.scale, from.z);
        const b = point(next, from.scale, from.z);
        const c = point(next, to.scale, to.z);
        const d = point(current, to.scale, to.z);
        this.triangle(a, c, b, rgb);
        this.triangle(a, d, c, rgb);
      }
    }
  }

  // The headband is an open curved strip, so each segment gets its own front and back face.
  arc(inner: number, outer: number, depth: number, tint: string) {
    const rgb = color(tint);
    const steps = 64;
    const start = -0.15;
    const end = Math.PI + 0.15;
    const point = (angle: number, radius: number, z: number): Vec3 => [Math.cos(angle) * radius, Math.sin(angle) * radius - 0.18, z];
    for (let i = 0; i < steps; i++) {
      const a = start + (i / steps) * (end - start);
      const b = start + ((i + 1) / steps) * (end - start);
      const quad = (p: Vec3, q: Vec3, r: Vec3, s: Vec3) => {
        this.triangle(p, q, r, rgb);
        this.triangle(p, r, s, rgb);
      };
      quad(point(a, inner, depth / 2), point(a, outer, depth / 2), point(b, outer, depth / 2), point(b, inner, depth / 2));
      quad(point(b, inner, -depth / 2), point(b, outer, -depth / 2), point(a, outer, -depth / 2), point(a, inner, -depth / 2));
      quad(point(a, outer, depth / 2), point(a, outer, -depth / 2), point(b, outer, -depth / 2), point(b, outer, depth / 2));
      quad(point(b, inner, depth / 2), point(b, inner, -depth / 2), point(a, inner, -depth / 2), point(a, inner, depth / 2));
      if (i === 0) quad(point(a, inner, depth / 2), point(a, inner, -depth / 2), point(a, outer, -depth / 2), point(a, outer, depth / 2));
      if (i === steps - 1) quad(point(b, outer, depth / 2), point(b, outer, -depth / 2), point(b, inner, -depth / 2), point(b, inner, depth / 2));
    }
  }
}

function roundedRect(width: number, height: number, radius: number): Point[] {
  const points: Point[] = [];
  for (let corner = 0; corner < 4; corner++) {
    const angle = corner * Math.PI / 2;
    const cx = (corner === 0 || corner === 3 ? 1 : -1) * (width / 2 - radius);
    const cy = (corner < 2 ? 1 : -1) * (height / 2 - radius);
    for (let i = 0; i <= 10; i++) {
      const theta = angle + (i / 10) * Math.PI / 2;
      points.push([cx + Math.cos(theta) * radius, cy + Math.sin(theta) * radius]);
    }
  }
  return points;
}

function circle(radius: number): Point[] {
  return Array.from({ length: 40 }, (_, i) => [Math.cos(i * Math.PI / 20) * radius, Math.sin(i * Math.PI / 20) * radius]);
}

function bezierOutline(start: Point, curves: [Point, Point, Point][]): Point[] {
  const points: Point[] = [start];
  let from = start;
  for (const [control1, control2, to] of curves) {
    for (let step = 1; step <= 12; step++) {
      const t = step / 12;
      const s = 1 - t;
      const interpolate = (axis: 0 | 1) => s ** 3 * from[axis] + 3 * s ** 2 * t * control1[axis] + 3 * s * t ** 2 * control2[axis] + t ** 3 * to[axis];
      points.push([interpolate(0), interpolate(1)]);
    }
    from = to;
  }
  return points;
}

export function buildSolidModel(kind: ModelKind): Float32Array {
  const mesh = new Mesh();
  if (kind === "controller") {
    const outline = bezierOutline([0, 0.95], [
      [[-0.8, 0.95], [-1.5, 1.2], [-1.95, 0.9]],
      [[-2.4, 0.65], [-2.65, -1.35], [-2.18, -1.5]],
      [[-1.7, -1.65], [-1.35, -0.55], [-0.85, -0.5]],
      [[-0.4, -0.48], [0.4, -0.48], [0.85, -0.5]],
      [[1.35, -0.55], [1.7, -1.65], [2.18, -1.5]],
      [[2.65, -1.35], [2.4, 0.65], [1.95, 0.9]],
      [[1.5, 1.2], [0.8, 0.95], [0, 0.95]],
    ]);
    mesh.solid(outline, 0.64, "#a3a6af", [0, 0.15, 0]);
    mesh.solid(roundedRect(1.3, 0.64, 0.15), 0.1, "#787c88", [0, 0.65, 0.36], 0.03);
    for (const x of [-0.75, 0.75]) {
      mesh.solid(circle(0.39), 0.16, "#747885", [x, -0.25, 0.39], 0.04);
      mesh.solid(circle(0.26), 0.24, "#555b69", [x, -0.25, 0.57], 0.05);
    }
    mesh.solid(roundedRect(0.62, 0.2, 0.04), 0.13, "#5f6471", [-1.5, 0.45, 0.4], 0.03);
    mesh.solid(roundedRect(0.2, 0.62, 0.04), 0.13, "#5f6471", [-1.5, 0.45, 0.4], 0.03);
    for (let i = 0; i < 4; i++) {
      const theta = i * Math.PI / 2;
      mesh.solid(circle(0.13), 0.13, "#636976", [1.5 + Math.cos(theta) * 0.3, 0.45 + Math.sin(theta) * 0.3, 0.4], 0.03);
    }
    mesh.solid(circle(0.09), 0.05, "#d0d2d9", [0, 0.08, 0.36], 0.01);
  } else if (kind === "headphones") {
    mesh.arc(1.24, 1.6, 0.46, "#d7efa0");
    for (const side of [-1, 1]) {
      mesh.solid(roundedRect(0.28, 1, 0.12), 0.28, "#c5df8e", [side * 1.44, -0.47, 0], 0.05);
      mesh.solid(roundedRect(0.67, 1.5, 0.3), 0.65, "#d7efa0", [side * 1.45, -0.9, 0.05], 0.1);
      mesh.solid(roundedRect(0.53, 1.24, 0.25), 0.16, "#adc97d", [side * 1.45, -0.9, 0.43], 0.04);
      mesh.solid(roundedRect(0.35, 0.91, 0.17), 0.06, "#bdd88b", [side * 1.45, -0.9, 0.53], 0.02);
    }
  } else {
    const outline = bezierOutline([-1.5, 1.22], [
      [[-2.05, 1.22], [-2.2, 0.95], [-2.2, 0.55]],
      [[-2.2, 0.05], [-2.2, -0.45], [-2.2, -0.65]],
      [[-2.2, -1.12], [-1.9, -1.25], [-1.5, -1.25]],
      [[-1.5, -1.5], [-1.7, -1.9], [-1.65, -1.9]],
      [[-1.2, -1.8], [-0.8, -1.45], [-0.6, -1.25]],
      [[0, -1.25], [1.2, -1.25], [1.5, -1.25]],
      [[2.05, -1.25], [2.2, -0.95], [2.2, -0.55]],
      [[2.2, 0], [2.2, 0.4], [2.2, 0.55]],
      [[2.2, 1.05], [1.95, 1.22], [1.5, 1.22]],
      [[0.5, 1.22], [-0.5, 1.22], [-1.5, 1.22]],
    ]);
    mesh.solid(outline, 0.58, "#b9ddf2", [0, 0.2, 0], 0.14);
    for (const x of [-0.72, 0, 0.72]) mesh.solid(circle(0.15), 0.07, "#82b1cf", [x, 0.2, 0.32], 0.025);
  }
  return new Float32Array(mesh.vertices);
}
